// Copied from the design studio waveform timeline component.
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useLayoutEffect, useRef } from 'react';

const TICK_MS = 40;
const BAR_W = 1.5;
const GAP = 1.5;
const STEP = BAR_W + GAP;
const HEIGHT = 28;
const MID = HEIGHT / 2;

const SPRING_STIFFNESS = 0.18;
const SPRING_DAMPING = 0.55;

function drawBar(ctx: CanvasRenderingContext2D, x: number, amp: number): void {
    if (amp < 0.01) {
        const r = BAR_W / 2;
        ctx.globalAlpha = 0.25;
        ctx.beginPath();
        ctx.arc(x + r, MID, r, 0, Math.PI * 2);
        ctx.fill();
    } else {
        const barH = Math.min(HEIGHT, Math.max(2, amp * HEIGHT));
        const y = (HEIGHT - barH) / 2;
        ctx.globalAlpha = Math.min(1, 0.3 + amp * 0.7);
        ctx.beginPath();
        ctx.roundRect(x, y, BAR_W, barH, 0.75);
        ctx.fill();
    }
}

function updateSprings(targets: number[], positions: number[], velocities: number[], dtNorm: number): void {
    for (let i = 0; i < positions.length; i++) {
        const target = targets[i] ?? 0;
        const pos = positions[i] ?? 0;
        const vel = velocities[i] ?? 0;
        const force = (target - pos) * SPRING_STIFFNESS * dtNorm;
        const newVel = (vel + force) * SPRING_DAMPING;
        const newPos = pos + newVel * dtNorm;
        if (Math.abs(newVel) < 0.0005 && Math.abs(target - newPos) < 0.001) {
            positions[i] = target;
            velocities[i] = 0;
        } else {
            velocities[i] = newVel;
            positions[i] = newPos;
        }
    }
}

type WaveformState = {
    targets: number[];
    positions: number[];
    velocities: number[];
    lastTick: number;
    prevTs: number;
    scrollOffset: number;
};

function tickWaveform(state: WaveformState, ts: number, visibleBars: number, getAmplitude: () => number): number {
    const dtMs = state.prevTs === 0 ? 16 : Math.min(ts - state.prevTs, 50);
    state.prevTs = ts;
    const dtNorm = dtMs / 16.67;

    const elapsed = state.lastTick === 0 ? 0 : ts - state.lastTick;
    if (state.lastTick === 0 || elapsed >= TICK_MS) {
        state.targets.push(getAmplitude());
        state.positions.push(0);
        state.velocities.push(0);
        if (state.targets.length > visibleBars) {
            state.targets.shift();
            state.positions.shift();
            state.velocities.shift();
        }
        state.scrollOffset = 0;
        state.lastTick = ts;
    } else {
        state.scrollOffset = (elapsed / TICK_MS) * STEP;
    }

    updateSprings(state.targets, state.positions, state.velocities, dtNorm);
    return state.scrollOffset;
}

function renderBars(
    ctx: CanvasRenderingContext2D,
    cssW: number,
    positions: number[],
    scrollOffset: number,
    color: string,
): void {
    ctx.clearRect(0, 0, cssW, HEIGHT);
    ctx.fillStyle = color;

    const startX = cssW - (positions.length - 1) * STEP - scrollOffset;
    for (let i = 0; i < positions.length; i++) {
        const x = startX + i * STEP;
        if (x < -BAR_W || x > cssW) continue;
        drawBar(ctx, x, Math.max(0, positions[i] ?? 0));
    }
    ctx.globalAlpha = 1;
}

type WaveformTimelineProps = {
    readonly getAmplitude: () => number;
    readonly paused?: boolean;
};

export const WaveformTimeline: FunctionComponent<WaveformTimelineProps> = ({ getAmplitude, paused = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animRef = useRef(0);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;

    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (is.null(canvas)) {
            return;
        }
        const maybeCtx = canvas.getContext('2d');
        if (is.null(maybeCtx)) {
            return;
        }
        const ctx = maybeCtx;

        const dpr = window.devicePixelRatio || 1;
        const cssW = canvas.getBoundingClientRect().width;
        if (cssW === 0) {
            return;
        }

        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(HEIGHT * dpr);
        ctx.scale(dpr, dpr);

        const visibleBars = Math.ceil(cssW / STEP) + 2;
        const state: WaveformState = {
            targets: Array.from({ length: visibleBars }, () => 0),
            positions: Array.from({ length: visibleBars }, () => 0),
            velocities: Array.from({ length: visibleBars }, () => 0),
            lastTick: 0,
            prevTs: 0,
            scrollOffset: 0,
        };

        const color = getComputedStyle(canvas).getPropertyValue('--color-error-600').trim() || '#d92d20';

        function loop(ts: number): void {
            if (pausedRef.current) {
                animRef.current = requestAnimationFrame(loop);
                return;
            }

            const offset = tickWaveform(state, ts, visibleBars, getAmplitude);
            renderBars(ctx, cssW, state.positions, offset, color);
            animRef.current = requestAnimationFrame(loop);
        }

        animRef.current = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(animRef.current);
    }, [getAmplitude]);

    return <canvas ref={canvasRef} className="flex-1" style={{ height: HEIGHT, width: '100%' }} />;
};
